package pl.michal_cyran.brand_hub.subpages.dayMathAd.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaBoxOpen
import com.varabyte.kobweb.silk.components.icons.fa.FaBus
import com.varabyte.kobweb.silk.components.icons.fa.FaCircleCheck
import com.varabyte.kobweb.silk.components.icons.fa.FaList
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.H6
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import pl.michal_cyran.brand_hub.CardStyle
import pl.michal_cyran.brand_hub.FeaturedCardStyle
import pl.michal_cyran.brand_hub.FeaturesGridStyle
import pl.michal_cyran.brand_hub.components.widgets.VisibilityAware
import pl.michal_cyran.brand_hub.components.widgets.revealFadeScale
import pl.michal_cyran.brand_hub.components.widgets.revealFadeSlideUp
import pl.michal_cyran.brand_hub.components.widgets.revealFadeSlideUpScale
import pl.michal_cyran.brand_hub.components.widgets.revealScaleIn
import pl.michal_cyran.brand_hub.components.widgets.staggerDelay
import pl.michal_cyran.brand_hub.subpages.dayMathAd.composables.ExplanationCard
import pl.michal_cyran.brand_hub.subpages.dayMathAd.composables.QuestionFormCard
import pl.michal_cyran.brand_hub.toSitePalette

@Composable
fun DayMathTestsFeature() {
    val cm = ColorMode.current.toSitePalette()

    Column(
        modifier = Modifier.fillMaxWidth().id("tests"),
        verticalArrangement = Arrangement.spacedBy(2.cssRem)
    ) {
        VisibilityAware(modifier = Modifier.fillMaxWidth()) { isVisible ->
            Column(verticalArrangement = Arrangement.spacedBy(0.5.cssRem)) {
                H6(
                    Modifier
                        .revealFadeSlideUp(isVisible, delayMs = 0)
                        .toAttrs()
                ) {
                    Text("Inteligentne testy")
                }

                H1(
                    Modifier
                        .revealFadeSlideUp(isVisible, delayMs = 120)
                        .toAttrs()
                ) {
                    SpanText(
                        "Dopasuj naukę do swojego ",
                        modifier = Modifier.color(cm.textMuted)
                    )
                    SpanText(
                        "rytmu dnia.",
                        modifier = Modifier.color(cm.primary)
                    )
                }
            }
        }

        SimpleGrid(
            numColumns(1, md = 2),
            modifier = FeaturesGridStyle.toModifier().fillMaxWidth().gap(2.cssRem)
        ) {
            // Left card — three question format cards
            VisibilityAware(modifier = CardStyle.toModifier()) { isVisible ->
                Column(
                    modifier = Modifier
                        .revealFadeSlideUpScale(isVisible, delayMs = 0),
                    verticalArrangement = Arrangement.spacedBy(1.cssRem)
                ) {
                    H3 {
                        Text("Trzy formaty zadań")
                    }

                    Row(
                        horizontalArrangement = Arrangement.spacedBy(1.5.cssRem)
                    ) {
                        val formats = listOf(
                            Triple(@Composable{ FaList() }, "Pytania ABCD", "Klasyczne wybieranie odpowiedzi."),
                            Triple(@Composable{ FaCircleCheck() }, "Prawda / fałsz", "Błyskawiczna weryfikacja wiedzy."),
                            Triple(@Composable{ FaBoxOpen() }, "Zadania otwarte", "Pełna analiza i rozwiązywanie.")
                        )
                        formats.forEachIndexed { index, (icon, title, subtitle) ->
                            QuestionFormCard(
                                icon = icon,
                                title = title,
                                subtitle = subtitle,
                                modifier = Modifier
                                    .revealFadeScale(
                                        isVisible,
                                        delayMs = staggerDelay(index, step = 120, offset = 200)
                                    )
                            )
                        }
                    }
                }
            }

            // Featured card
            VisibilityAware(modifier = FeaturedCardStyle.toModifier()) { isVisible ->
                Column(
                    modifier = Modifier
                        .padding(leftRight = 1.cssRem, topBottom = 2.cssRem)
                        .revealFadeSlideUpScale(isVisible, delayMs = 150),
                    verticalArrangement = Arrangement.spacedBy(2.cssRem),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    FaBus(
                        size = IconSize.LG,
                        modifier = Modifier
                            .color(cm.textOnPrimary)
                            .revealScaleIn(isVisible, delayMs = 350)
                    )

                    H3(
                        Modifier
                            .color(cm.textOnPrimary)
                            .revealFadeSlideUp(isVisible, delayMs = 450)
                            .toAttrs()
                    ) {
                        Text("W autobusie czy przy biurku?")
                    }
                    P(
                        Modifier
                            .color(cm.textOnPrimary)
                            .revealFadeSlideUp(isVisible, delayMs = 550)
                            .toAttrs()
                    ) {
                        Text("Wybierz zadania \"w pamięci\" gdy podróżujesz lub \"z kartką\", gdy masz czas na pełne skupienie.")
                    }

                    HorizontalDivider(
                        modifier = Modifier.weight(1f)
                    )

                    P(
                        Modifier
                            .fontWeight(800)
                            .color(cm.textOnPrimary)
                            .revealFadeSlideUp(isVisible, delayMs = 650)
                            .toAttrs()
                    ) {
                        Text("UNIKALNA FUNKCJA")
                    }
                }
            }
        }

        VisibilityAware(modifier = Modifier.fillMaxWidth()) { isVisible ->
            ExplanationCard(
                icon = "lightbulb",
                title = "Nigdy nie utkniesz w miejscu.",
                body = "Każde zadanie posiada szczegółowe wyjaśnienie \"krok po kroku\" oraz pomocne wskazówki, które naprowadzą Cię na właściwy tor myślenia bez podawania gotowca.",
                modifier = Modifier.revealFadeSlideUpScale(isVisible, delayMs = 0)
            )
        }
    }
}